"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import LanguageSwitcher from "@/app/components/LanguageSwitcher";
import UserProfileDropdown from "@/app/components/UserProfileDropdown";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { useInformation } from "@/context/InformationContext";
import { usePathname, useParams } from "next/navigation";
import { getAppVersion } from "@/utils/version";
import isPermitted from "@/utils/isPermitted";
import { PAGE_ROUTES } from "@/constants/PermissionsConstants";
import {
  getClientTopBarLogoUrl,
  getInvitationTopBarLogoUrl,
} from "@/utils/clientBranding";
import { invitationsApi } from "@/api/invitations";
import { CALL_BLOCKED_TOOLTIP_KEY } from "@/utils/callLimits";
import useCallLimitsStatus from "@/customHooks/useCallLimitsStatus";
import { useSidebarStore } from "@/store/sidebarStore";

export default function AppHeader() {
  const { t } = useTranslation();
  const { user, allPermissions } = useAuth();
  const { isTrainingInFullScreen } = useInformation();
  const { toggleSidebar } = useSidebarStore();
  // get current route
  const currentRoute = usePathname();
  const params = useParams();
  const isTrainingRoute = currentRoute.includes("/training");
  const isRegisterRoute = currentRoute.includes("/register");

  // The mobile menu button is only useful when the sidebar exists for this
  // user and we're not on a training/auth screen (which hide the sidebar).
  const canViewSidebar = isPermitted(
    user,
    PAGE_ROUTES.SIDEBAR,
    allPermissions
  );
  const showMobileMenuButton =
    canViewSidebar && !isTrainingRoute && !isRegisterRoute;
  const [customLogoUrl, setCustomLogoUrl] = useState(null);
  const [customLogoStyle, setCustomLogoStyle] = useState(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const trainingClientId = user?.sf_clients?.[0]?.id;
  const { isLoadingLimits, isCallBlockedByLimits } =
    useCallLimitsStatus(trainingClientId);
  const isStartNewCallDisabled = isLoadingLimits || isCallBlockedByLimits;
  const canStartCallFromTopHeader = isPermitted(
    user,
    PAGE_ROUTES.TOP_HEADER_START_CALL,
    allPermissions
  );
  const callBlockedTooltip = t(CALL_BLOCKED_TOOLTIP_KEY, {
    defaultValue: "Call unavailable: one or more client limits have been reached.",
  });

  useEffect(() => {
    const fetchLogo = async () => {
      if (user) {
        // User is logged in - use user customizations
        setIsLoadingLogo(true);
        setHasAttemptedLoad(false);
        try {
          const logoData = await getClientTopBarLogoUrl(user);
          if (logoData) {
            setCustomLogoUrl(logoData.url);
            setCustomLogoStyle(logoData.style || null);
          } else {
            setCustomLogoUrl(null);
            setCustomLogoStyle(null);
          }
        } catch (error) {
          console.error("Failed to fetch logo:", error);
          setCustomLogoUrl(null);
          setCustomLogoStyle(null);
        } finally {
          setHasAttemptedLoad(true);
          setIsLoadingLogo(false);
        }
      } else if (isRegisterRoute && params?.code) {
        // On register route - use invitation customizations
        setIsLoadingLogo(true);
        setHasAttemptedLoad(false);
        try {
          // Fetch invitation data to get customizations
          const invitationResponse = await invitationsApi.validate(
            params.code
          );
          if (invitationResponse.data?.valid) {
            const logoData = await getInvitationTopBarLogoUrl(
              invitationResponse.data
            );
            if (logoData) {
              setCustomLogoUrl(logoData.url);
              setCustomLogoStyle(logoData.style || null);
            } else {
              setCustomLogoUrl(null);
              setCustomLogoStyle(null);
            }
          } else {
            setCustomLogoUrl(null);
            setCustomLogoStyle(null);
          }
        } catch (error) {
          console.error("Failed to fetch invitation logo:", error);
          setCustomLogoUrl(null);
          setCustomLogoStyle(null);
        } finally {
          setHasAttemptedLoad(true);
          setIsLoadingLogo(false);
        }
      } else {
        // Clear logo when user logs out or not on register route
        setCustomLogoUrl(null);
        setCustomLogoStyle(null);
        setIsLoadingLogo(false);
      }
    };
    fetchLogo();
  }, [user, isRegisterRoute, params?.code]);

  return (
    <header
      className={`w-full bg-white ${isTrainingInFullScreen ? "hidden" : "block"}`}
    >
      <div className="max-w-full mx-auto px-3 md:px-16 h-[68px] flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {showMobileMenuButton ? (
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={t("button.openMenu", { defaultValue: "Open menu" })}
              className="md:hidden flex items-center justify-center w-11 h-11 -ml-1 mr-1 rounded-[6px] hover:bg-nav-active-bg transition-colors"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#2d2d2d"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          ) : null}
          {isPermitted(user, PAGE_ROUTES.DASHBOARD, allPermissions) ? (
            <Link href="/dashboard">
              {isLoadingLogo || !hasAttemptedLoad ? (
                <div style={{ width: 97, height: 33 }} /> // Placeholder to prevent layout shift
              ) : customLogoUrl ? (
                <img
                  src={customLogoUrl}
                  alt="client logo"
                  onError={() => {
                    // Fallback to default if image fails to load
                    setCustomLogoUrl(null);
                    setCustomLogoStyle(null);
                  }}
                  style={{
                    // Spread all dynamic styles from API first
                    ...(customLogoStyle || {}),
                    // maxWidth and maxHeight from code ALWAYS take precedence
                    maxHeight: "66px", // Smaller than header to prevent scroll (header is 68px)
                    maxWidth: "512px", // 2x sidebar width (256px * 2)
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Image
                  src="/universal/talks-logo.svg"
                  alt="skillFull logo"
                  width={97}
                  height={33}
                  priority
                />
              )}
            </Link>
          ) : (
            <>
              {isLoadingLogo || (!hasAttemptedLoad && !isRegisterRoute) ? (
                <div style={{ width: 97, height: 33 }} /> // Placeholder to prevent layout shift
              ) : customLogoUrl ? (
                <img
                  src={customLogoUrl}
                  alt="client logo"
                  onError={() => {
                    // Fallback to default if image fails to load
                    setCustomLogoUrl(null);
                    setCustomLogoStyle(null);
                  }}
                  style={{
                    // Spread all dynamic styles from API first
                    ...(customLogoStyle || {}),
                    // maxWidth and maxHeight from code ALWAYS take precedence
                    maxHeight: "66px", // Smaller than header to prevent scroll
                    maxWidth: "512px", // 2x sidebar width
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Image
                  src="/universal/talks-logo.svg"
                  alt="skillFull logo"
                  width={97}
                  height={33}
                  priority
                />
              )}
            </>
          )}
          {isPermitted(user, undefined, allPermissions) && hasAttemptedLoad ? (
            <span className="text-xs text-gray-500 font-mono">
              v{getAppVersion()}
            </span>
          ) : null}
        </div>
        <div className="flex items-center space-x-3">
          {canStartCallFromTopHeader ? (
            <Link
              href={isStartNewCallDisabled ? "#" : "/training"}
              onClick={(e) => {
                if (isStartNewCallDisabled) e.preventDefault();
              }}
            >
              <button
                type="button"
                disabled={isStartNewCallDisabled}
                aria-label={t("button.startNewCall")}
                title={
                  isCallBlockedByLimits
                    ? callBlockedTooltip
                    : t("button.startNewCall")
                }
                className={`gap-2 sm:gap-3.5 items-center px-2.5 sm:px-3 py-2 bg-accent-purple rounded-[6px] hover:bg-accent-purple-hover ${
                  isTrainingRoute || isRegisterRoute ? "hidden" : "flex"
                } ${isStartNewCallDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                style={{ boxShadow: "0px 4px 16px 0px #E4E4E4" }}
              >
                <Image
                  src="/sidebar/demo-icon.svg"
                  alt=""
                  width={20}
                  height={20}
                />
                {/* Label hidden on phones — icon-only keeps the top bar from overflowing */}
                <span className="hidden sm:inline text-app-purple text-[14px] font-bold leading-6 capitalize">
                  {t("button.startNewCall")}
                </span>
              </button>
            </Link>
          ) : null}

          <LanguageSwitcher />
          <UserProfileDropdown
            userName={
              user?.first_name
                ? `${user?.first_name} ${user?.sur_name ?? ""}`
                : ""
            }
            userEmail={user?.email}
            avatarSrc={"/universal/avatar.svg"}
          />
        </div>
      </div>
    </header>
  );
}